"use client";

import { c as _c } from "react/compiler-runtime";
import { cva, VariantProps } from "class-variance-authority";
import { FC, MouseEventHandler, PropsWithChildren } from "react";
import Link from "../internal/Link";
import CardButtonBody from "./CardButtonBody";
import CardButtonFooter from "./CardButtonFooter";
import CardButtonTitle from "./CardButtonTitle";
import { CardButtonContext } from "./utilities";
const buttonStyles = cva("group text-left flex flex-col justify-between min-w-[auto] m:min-w-[320px] transition cursor-pointer focusable", {
  variants: {
    variant: {
      outline_primary: "hover:text-primary-800 border border-neutral-50 hover:border-primary-800 bg-neutral-0 hover:bg-neutral-12 active:bg-neutral-25",
      marigold: "text-neutral-0 bg-marigold-100 hover:bg-marigold-525 active:bg-marigold-750",
      primary: "text-neutral-0 bg-primary-800 hover:bg-primary-825 active:bg-primary-850",
      purple: "text-neutral-0 bg-purple-900 hover:bg-purple-850 active:bg-purple-775"
    },
    size: {
      sm: "rounded-s p-3s",
      lg: "rounded-m p-m min-h-[320px]"
    }
  },
  defaultVariants: {
    variant: "primary",
    size: "lg"
  }
});
export type CardButtonProps = {
  href?: string | null;
  className?: string;
  id?: string;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  testId?: string;
} & VariantProps<typeof buttonStyles>;
const CardButton: FC<PropsWithChildren<CardButtonProps>> = t0 => {
  const $ = _c(9);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <CardButtonComponent {...props}>{children}</CardButtonComponent>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <CardButtonContext.Provider value={props}>{t1}</CardButtonContext.Provider>;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
const CardButtonComponent: FC<PropsWithChildren<CardButtonProps>> = t0 => {
  const $ = _c(21);
  const {
    href,
    variant,
    children,
    className,
    id,
    onClick,
    testId,
    size
  } = t0;
  if (href) {
    let t1;
    if ($[0] !== className || $[1] !== size || $[2] !== variant) {
      t1 = buttonStyles({
        size,
        variant,
        className
      });
      $[0] = className;
      $[1] = size;
      $[2] = variant;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== children || $[5] !== href || $[6] !== id || $[7] !== onClick || $[8] !== t1 || $[9] !== testId) {
      t2 = <Link data-testid={testId} onClick={onClick} href={href} id={id} className={t1}>{children}</Link>;
      $[4] = children;
      $[5] = href;
      $[6] = id;
      $[7] = onClick;
      $[8] = t1;
      $[9] = testId;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  } else {
    let t1;
    if ($[11] !== className || $[12] !== size || $[13] !== variant) {
      t1 = buttonStyles({
        size,
        variant,
        className
      });
      $[11] = className;
      $[12] = size;
      $[13] = variant;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    let t2;
    if ($[15] !== children || $[16] !== id || $[17] !== onClick || $[18] !== t1 || $[19] !== testId) {
      t2 = <button data-testid={testId} onClick={onClick} type="button" id={id} className={t1}>{children}</button>;
      $[15] = children;
      $[16] = id;
      $[17] = onClick;
      $[18] = t1;
      $[19] = testId;
      $[20] = t2;
    } else {
      t2 = $[20];
    }
    return t2;
  }
};
export default Object.assign(CardButton, {
  Title: CardButtonTitle,
  Body: CardButtonBody,
  Footer: CardButtonFooter
});